import React from 'react'

const isObject = value => !!value && typeof value === 'object' && !Array.isArray(value)

const withClass = (ui, className) => ({ ...ui, classNames: [ui.classNames, className].filter(Boolean).join(' ') })

const hideClosedSections = (uiSchema, formData, openSections, filledSections) =>
  Object.fromEntries(
    Object.entries(uiSchema).map(([key, ui]) => {
      if (key.startsWith('ui:') || !isObject(ui)) return [key, ui]
      if (ui['ui:field'] === 'section') return [key, withClass(ui, 'form-section')]
      const section = ui['ui:options']?.section
      if (!section) return [key, hideClosedSections(ui, formData?.[key], openSections, filledSections)]
      if (formData?.[key]) filledSections[section] = (filledSections[section] || 0) + 1
      if (!openSections[section]) return [key, { ...ui, 'ui:widget': 'hidden' }]
      return [key, withClass(ui, 'form-section-member')]
    })
  )

/**
 * Applique les sections à un uiSchema, à toute profondeur. Les champs d'une section nomment son en-tête dans
 * `ui:options.section`, y compris depuis un autre objet que celui de l'en-tête : ils s'affichent là où rjsf les place,
 * donc à déclarer après l'en-tête dans l'ordre de rendu. Tant que la section est repliée, ses champs
 * reçoivent `ui:widget: hidden` — rjsf les enveloppe alors dans un bloc masqué, quel que soit leur `ui:field` ; affichés,
 * ils reçoivent la classe `form-section-member`, qui les relie visuellement à leur en-tête (classe `form-section`).
 * Compte aussi, par section, les champs renseignés.
 */
export const applySections = (uiSchema, formData, openSections) => {
  const filledSections = {}
  return { uiSchema: hideClosedSections(uiSchema, formData, openSections, filledSections), filledSections }
}

/**
 * En-tête d'une section : le titre de la propriété sert de libellé, et le nom de la propriété est celui que ses champs
 * déclarent dans `ui:options.section`. Un clic déplie ou replie la section, et le nombre de champs renseignés
 * s'affiche quand elle est repliée.
 * L'état est tenu par le formulaire et passé par le contexte. En lecture seule, la section disparaît tant qu'aucun de
 * ses champs n'a de valeur, n'ayant rien à montrer.
 */
export const Section = ({ schema, name, formContext, disabled }) => {
  const filled = formContext?.filledSections?.[name] || 0
  if (disabled && !filled) return null

  const open = !!formContext?.openSections?.[name]
  return (
    <button
      type="button"
      className={'form-section-title' + (open ? ' open' : '')}
      aria-expanded={open}
      onClick={() => formContext?.toggleSection?.(name)}
    >
      <i className={`fa fa-chevron-${open ? 'down' : 'right'}`} />
      <span className="form-section-label">{schema.title}</span>
      <span className="form-section-hint">
        {!open && filled > 0 && `${filled} renseigné${filled > 1 ? 's' : ''} · `}
        {open ? 'Masquer' : 'Afficher'}
      </span>
    </button>
  )
}
